import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getActivityLogs, getUsers } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Activity, User, CheckSquare, LogIn, Key, UserPlus, Edit, Trash2, Database, X,
  Paperclip, ShieldCheck, Tag, FileSpreadsheet, RefreshCw, Download,
} from 'lucide-react';

const ENTITY_TYPES = [
  { value: 'user', label: 'User' },
  { value: 'task', label: 'Task' },
  { value: 'category', label: 'Category' },
  { value: 'project', label: 'Project' },
  { value: 'backup', label: 'Backup' },
];

const ACTIONS = [
  'TASK_CREATED', 'TASK_UPDATED', 'TASK_DELETED', 'STATUS_CHANGED',
  'TASK_APPROVED', 'TASK_REJECTED', 'BULK_TASK_UPLOAD',
  'USER_LOGIN', 'USER_SIGNUP', 'USER_CREATED', 'USER_UPDATED', 'USER_DELETED',
  'PASSWORD_CHANGED', 'PASSWORD_RESET',
  'CATEGORY_CREATED', 'CATEGORY_DELETED',
  'BACKUP_CREATED', 'EXCEL_BACKUP',
];

export const ActivityLogsPage = () => {
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(100);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (entityTypeFilter) params.entity_type = entityTypeFilter;
      if (actionFilter) params.action = actionFilter;
      if (userFilter) params.user_id = userFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await getActivityLogs(params);
      setLogs(res.data);
    } catch (e) {
      console.error('Failed to fetch logs:', e);
    } finally {
      setLoading(false);
    }
  }, [entityTypeFilter, actionFilter, userFilter, dateFrom, dateTo, limit]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    // Load users for the user filter (Admin: dept users, Super Admin: all)
    (async () => {
      try {
        const res = await getUsers({ status: 'Active' });
        setUsers(res.data);
      } catch (e) { /* noop */ }
    })();
  }, []);

  const filteredLogs = useMemo(() => {
    if (!search.trim()) return logs;
    const q = search.toLowerCase();
    return logs.filter(l =>
      (l.details || '').toLowerCase().includes(q) ||
      (l.user_name || '').toLowerCase().includes(q) ||
      (l.action || '').toLowerCase().includes(q)
    );
  }, [logs, search]);

  const clearFilters = () => {
    setEntityTypeFilter(''); setActionFilter(''); setUserFilter('');
    setDateFrom(''); setDateTo(''); setSearch('');
  };

  const getActionIcon = (action) => {
    if (action.includes('LOGIN')) return <LogIn className="h-4 w-4" />;
    if (action.includes('APPROVED') || action.includes('REJECTED')) return <ShieldCheck className="h-4 w-4" />;
    if (action.includes('STATUS_CHANGED')) return <RefreshCw className="h-4 w-4" />;
    if (action.includes('SIGNUP') || action.includes('USER_CREATED')) return <UserPlus className="h-4 w-4" />;
    if (action.includes('CATEGORY')) return <Tag className="h-4 w-4" />;
    if (action.includes('BULK')) return <FileSpreadsheet className="h-4 w-4" />;
    if (action.includes('UPDATED') || action.includes('CHANGED')) return <Edit className="h-4 w-4" />;
    if (action.includes('DELETED')) return <Trash2 className="h-4 w-4" />;
    if (action.includes('PASSWORD')) return <Key className="h-4 w-4" />;
    if (action.includes('BACKUP')) return <Database className="h-4 w-4" />;
    if (action.includes('TASK')) return <CheckSquare className="h-4 w-4" />;
    if (action.includes('ATTACH')) return <Paperclip className="h-4 w-4" />;
    return <Activity className="h-4 w-4" />;
  };

  const getActionColor = (action) => {
    if (action.includes('DELETED') || action.includes('REJECTED')) return 'bg-red-500/15 text-red-600 border-red-500/20';
    if (action.includes('APPROVED') || action.includes('CREATED') || action.includes('SIGNUP')) return 'bg-emerald-500/15 text-emerald-600 border-emerald-500/20';
    if (action.includes('UPDATED') || action.includes('CHANGED')) return 'bg-blue-500/15 text-blue-600 border-blue-500/20';
    if (action.includes('LOGIN')) return 'bg-violet-500/15 text-violet-600 border-violet-500/20';
    return 'bg-gray-500/15 text-gray-600 border-gray-500/20';
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12" data-testid="no-access">
        <p className="text-muted-foreground">Activity Logs are available only to Admin and Super Admin.</p>
      </div>
    );
  }

  const exportCSV = () => {
    if (!filteredLogs.length) return;
    const headers = ['Timestamp', 'User', 'Action', 'Entity Type', 'Entity ID', 'Details'];
    const rows = filteredLogs.map(l => [
      new Date(l.timestamp).toISOString(),
      l.user_name || '',
      l.action || '',
      l.entity_type || '',
      l.entity_id || '',
      (l.details || '').replace(/\r?\n/g, ' '),
    ]);
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity_logs_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const hasFilters = entityTypeFilter || actionFilter || userFilter || dateFrom || dateTo || search;
  const scopeLabel = isSuperAdmin ? 'All departments' : `Department: ${user?.department}`;

  return (
    <div className="space-y-6" data-testid="activity-logs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Activity Logs</h1>
          <p className="text-muted-foreground mt-1 text-sm">{scopeLabel} · {filteredLogs.length} of {logs.length} entries</p>
        </div>
        <Button
          variant="outline"
          onClick={exportCSV}
          disabled={filteredLogs.length === 0}
          data-testid="export-logs-csv-btn"
        >
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-border/50">
        <CardContent className="p-4 space-y-3">
          <Input
            placeholder="Search details, user, or action..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="logs-search"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Select value={entityTypeFilter || 'all'} onValueChange={(v) => setEntityTypeFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40 h-9" data-testid="logs-entity-filter">
                <SelectValue placeholder="Entity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {ENTITY_TYPES.map(e => (
                  <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={actionFilter || 'all'} onValueChange={(v) => setActionFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-48 h-9" data-testid="logs-action-filter">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {ACTIONS.map(a => (
                  <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={userFilter || 'all'} onValueChange={(v) => setUserFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-48 h-9" data-testid="logs-user-filter">
                <SelectValue placeholder="User" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-1">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-40 h-9"
                data-testid="logs-date-from"
              />
              <span className="text-xs text-muted-foreground">→</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-40 h-9"
                data-testid="logs-date-to"
              />
            </div>

            <Select value={limit.toString()} onValueChange={(v) => setLimit(parseInt(v))}>
              <SelectTrigger className="w-28 h-9" data-testid="logs-limit-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="200">200</SelectItem>
                <SelectItem value="500">500</SelectItem>
              </SelectContent>
            </Select>

            {hasFilters && (
              <Button variant="ghost" size="sm" className="h-9 text-xs gap-1" onClick={clearFilters} data-testid="logs-clear-filters">
                <X className="h-3 w-3" />Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Logs */}
      <Card className="border-border/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No activity logs found</p>
            </div>
          ) : (
            <ScrollArea className="h-[640px]">
              <div className="divide-y divide-border">
                {filteredLogs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-start gap-4 p-4 hover:bg-accent/50 transition-colors"
                    data-testid={`log-item-${log.id}`}
                  >
                    <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center shrink-0">
                      {getActionIcon(log.action)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`text-[10px] ${getActionColor(log.action)}`}>
                          {log.action.replace(/_/g, ' ')}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]">{log.entity_type}</Badge>
                      </div>
                      <p className="mt-1.5 text-sm break-words">{log.details}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {log.user_name}
                        </span>
                        <span>·</span>
                        <span>{new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
